import { useTranslation } from 'react-i18next'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useCustomError } from 'src/utils/custom-errors.hook'
import { getReservedResourceGuestsSchema, ReservedResourceGuestsSchema } from './schema'
import { GContext } from '../../../../context/reservation-context.types'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'

export const useReservedResourceGuestsForm = (
  //
  hotelDate: Date,
  start: Date,
  end: Date,
  guests: GContext[],
  onSubmit: (data: ReservedResourceGuestsSchema) => Promise<void>,
) => {
  const { t } = useTranslation()
  const formatDate = useFormatDate()
  const schema = getReservedResourceGuestsSchema(t, formatDate)
  const customErrorMap = useCustomError()
  z.setErrorMap(customErrorMap)
  const {
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitted },
  } = useForm<ReservedResourceGuestsSchema>({ resolver: zodResolver(schema) })

  const submitHandler: SubmitHandler<ReservedResourceGuestsSchema> = useCallback(
    async (data: ReservedResourceGuestsSchema) => {
      await onSubmit(data)
    },
    [onSubmit],
  )

  const submitForm = useCallback(handleSubmit(submitHandler), [handleSubmit, submitHandler])

  const value = watch()

  useEffect(() => {
    setValue('guests', guests, { shouldValidate: isSubmitted })
  }, [guests])

  useEffect(() => {
    setValue('start', start)
    setValue('end', end)
    setValue('hotelDate', hotelDate)
  }, [start, end, hotelDate])

  return { value, errors, submitForm }
}
